import * as React from 'react';
import { SimulatedChangeEvent } from '../../../hooks/useForm';
import { FComponent } from '../../../types/common';
import { compact, formatBytes } from '../../../utils';
import { Button } from '../Button';
import AddIcon from '../Icons/AddIcon';
import ImageIcon from '../Icons/ImageIcon';
import RoundedDocumentIcon from '../Icons/RoundedDocumentIcon';
import TrashcanIcon from '../Icons/TrashcanIcon';
import { FieldErrors } from './FieldErrors';
import { Modal, ModalProps } from '../Modal';
import {
  FileInputTarget,
  FileInputValue,
  useFileInput
} from '../../../hooks/UseFileInput';
import VideoIcon from '../Icons/VideoIcon';

type FileInputProps = {
  name: string;
  label?: string;
  acceptedFileExtensions: string[];
  maxSize: number;
  minSize?: number;
  multiple?: boolean;
  onChange: (event: SimulatedChangeEvent<FileInputTarget>) => void;
  value?: FileInputValue[];
  visited?: boolean;
  errors?: string[];
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
};

const FileInput: FComponent<FileInputProps, HTMLInputElement> = ({
  name,
  label,
  acceptedFileExtensions,
  multiple = false,
  maxSize,
  minSize = 0,
  onChange,
  value: filesLoaded,
  visited,
  errors,
  disabled = false,
  className = 'file-input',
  ariaLabel
}) => {
  const inputRef = React.useRef<HTMLInputElement>();
  const [previewImageUrl, setPreviewImageUrl] = React.useState<string>(null);
  const showAddButton = multiple || filesLoaded.length === 0;

  const { handleFileUpload, deleteFile, rejectedFiles, setRejectedFiles } = useFileInput({
    name,
    inputRef,
    acceptedFileExtensions,
    maxSize,
    minSize,
    onChange,
    value: filesLoaded,
    visited
  });

  return (
    <div className={className}>
      {label ? (
        <label htmlFor={name} className="field-label">
          {label}
        </label>
      ) : null}

      <input
        ref={inputRef}
        aria-label={ariaLabel}
        accept={acceptedFileExtensions.map(s => `.${s}`).join(',')}
        multiple={multiple}
        type="file"
        name={name}
        onChange={handleFileUpload}
        disabled={disabled}
      />
      {showAddButton ? (
        <Button
          className="add-file-button"
          secondary
          icon={<AddIcon />}
          onClick={() => inputRef.current.click()}>
          Add File{multiple ? 's' : ''}
        </Button>
      ) : null}
      {filesLoaded.length > 0 ? (
        <div className="thumbnails">
          {filesLoaded.map(({ url, file, extension, local }, index) => (
            <FilePreviewEntry
              key={url}
              index={index}
              url={url}
              file={file}
              local={local}
              extension={extension}
              onClick={() => setPreviewImageUrl(url)}
              onDelete={() => deleteFile(index)}
              disabled={disabled}
            />
          ))}
        </div>
      ) : null}
      <FieldErrors visited={visited} errors={errors} />

      <PreviewImageModal
        previewImageUrl={previewImageUrl}
        isOpen={!!previewImageUrl}
        toggle={() => setPreviewImageUrl(null)}
      />
      <RejectedFilesModal
        rejectedFiles={rejectedFiles}
        maxSize={maxSize}
        minSize={minSize}
        fileTypes={acceptedFileExtensions}
        isOpen={rejectedFiles.length > 0}
        toggle={() => setRejectedFiles([])}
      />
    </div>
  );
};

const PreviewImageModal: FComponent<
  Partial<ModalProps> & { previewImageUrl: string }
> = ({ previewImageUrl, isOpen, toggle }) => {
  // This is to prevent jerkiness when closing the modal
  const [imageUrl, setImageUrl] = React.useState(previewImageUrl);

  React.useEffect(() => {
    if (previewImageUrl) {
      setImageUrl(previewImageUrl);
    }
  }, [previewImageUrl]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} title="Preview">
      <img className="modal-preview-image" alt="preview of file" src={imageUrl} />
      <Button onClick={toggle}>Close</Button>
    </Modal>
  );
};

const RejectedFilesModal: FComponent<
  Partial<ModalProps> & {
    rejectedFiles: FileInputValue[];
    maxSize: number;
    minSize: number;
    fileTypes: string[];
  }
> = ({ rejectedFiles = [], maxSize, minSize, fileTypes, isOpen, toggle }) => {
  return (
    <Modal isOpen={isOpen} toggle={toggle} title="The following files are invalid">
      <div className="rejected-file-list-modal">
        <div className="file-list">
          {rejectedFiles.map(({ file, url, extension }) => (
            <div key={url}>
              {file.name}{' '}
              <span>
                (size: {formatBytes(file.size)}, type: {extension})
              </span>
            </div>
          ))}
        </div>
        <div className="legend">
          <span>File size max: {formatBytes(maxSize)}</span>
          <span>File size min: {formatBytes(minSize || 0)}</span>
          <span>Allowed file types: {fileTypes.join(', ')}</span>
        </div>
      </div>
      <Button onClick={toggle}>Close</Button>
    </Modal>
  );
};

type FilePreviewEntryProps = {
  url: string;
  index: number;
  file: File;
  extension: string;
  local: boolean;
  onClick: () => void;
  onDelete: (index: number) => void;
  disabled: boolean;
};

const FilePreviewEntry: FComponent<FilePreviewEntryProps> = ({
  url,
  index,
  extension,
  local,
  file,
  onClick,
  onDelete,
  disabled
}) => (
  <div className="file-preview">
    <button
      type="button"
      className="show-preview"
      disabled={!canPreview(extension)}
      onClick={onClick}>
      <Preview url={url} extension={extension} local={local} index={index} />
      <span>{compact(file?.name.toLowerCase() || `file ${index + 1}`)}</span>
    </button>
    {disabled ? null : (
      <button
        className="delete"
        type="button"
        onClick={() => onDelete(index)}
        aria-label={`delete file ${index + 1}`}>
        <TrashcanIcon />
      </button>
    )}
  </div>
);

type PreviewProps = {
  url: string;
  extension: string;
  local: boolean;
  index: number;
};

const Preview: FComponent<PreviewProps> = ({ url, extension, local, index }) => {
  if (canPreview(extension)) {
    if (local) {
      return <img className="image-preview" alt={`preview of file ${index}`} src={url} />;
    } else {
      return <ImageIcon />;
    }
  } else if (isVideo(extension)) {
    return <VideoIcon />;
  } else {
    return <RoundedDocumentIcon />;
  }
};

const canPreview = (extension: string) =>
  ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(extension.toLowerCase());

const isVideo = (extension: string) => ['mp4', 'mov'].includes(extension.toLowerCase());

export { FileInput };
